import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../../providers/SupabaseProvider';
import { useAuth } from '../../../providers/SupabaseProvider';
import { Loader2, X, Calendar, FileText, Umbrella } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';
import { format, getISOWeek, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { annotateVacationConflicts, formatVacationRange, VacationConflict, getVacationStatusLabel } from '../../../utils/vacationConflicts';
import { logDocumentExportForCurrentUser } from '../../../lib/auditLogger';

interface MonthlyListModalProps {
  show: boolean;
  onClose: () => void;
}

const MAIN_LOCATION_FILTER = '__main_location__';

interface OrganizationLocationOption {
  id: string;
  name: string;
}

type SampleForReport = {
  id: string;
  customer_id?: string | null;
  contract_id?: string | null;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  sample_type_name?: string | null;
  scheduled_date?: string | null;
  appointment_date?: string | null;
  status?: string | null;
  vacation_conflicts?: VacationConflict[];
  vacations_in_month?: VacationConflict[];
};

const VACATION_PLACEHOLDER = '-';
type CmykColor = readonly [number, number, number, number];

// CMYK-Tints für die Monatsliste: druckfreundlich und konsistent in PDF + UI.
const PDF_STATUS_ROW_FILL_CMYK: Record<string, CmykColor> = {
  appointed: [0, 0, 16, 0],
  collected: [14, 0, 0, 0],
  sent: [14, 0, 0, 0],
  received: [14, 0, 14, 0],
  nicht_erschienen: [0, 14, 14, 0],
  no_show: [0, 14, 14, 0],
};

const formatDateLabel = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('de-DE') : VACATION_PLACEHOLDER;

const formatPdfDateLabel = (value?: string | null) => {
  if (!value) {
    return VACATION_PLACEHOLDER;
  }

  const date = parseISO(value);
  if (!isValid(date)) {
    return formatDateLabel(value);
  }

  // Ergänzt Kürzel und Kalenderwoche direkt am Datum, damit die PDF-Liste kompakt bleibt.
  return `${format(date, 'dd.MM.yyyy', { locale: de })} (${format(date, 'EE', { locale: de })}, KW ${getISOWeek(date)})`;
};

const getStatusLabel = (status?: string | null) => {
  switch (status) {
    case 'scheduled':
      return 'Geplant';
    case 'appointed':
      return 'Einbestellt';
    case 'collected':
      return 'Entnommen';
    case 'sent':
      return 'Versendet';
    case 'received':
      return 'Befund erhalten';
    case 'nicht_erschienen':
    case 'no_show':
      return 'Nicht erschienen';
    default:
      return status ?? VACATION_PLACEHOLDER;
  }
};

const getPdfStatusRowFill = (status?: string | null) => {
  return status ? PDF_STATUS_ROW_FILL_CMYK[status] ?? null : null;
};

const getStatusBadgeClass = (status?: string | null) => {
  switch (status) {
    case 'appointed':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200';
    case 'collected':
    case 'sent':
      return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200';
    case 'received':
      return 'bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-200';
    case 'nicht_erschienen':
    case 'no_show':
      return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
    case 'scheduled':
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};

const getVacationSummary = (conflicts?: VacationConflict[]) => {
  if (!conflicts || conflicts.length === 0) {
    return VACATION_PLACEHOLDER;
  }

  const firstConflict = conflicts[0];
  const range = formatVacationRange(firstConflict);
  const statusLabel = getVacationStatusLabel(firstConflict.status);
  const baseLabel = `Urlaub${statusLabel ? ` (${statusLabel})` : ''}${range ? ` ${range}` : ''}`;

  if (conflicts.length > 1) {
    return `${baseLabel} (+${conflicts.length - 1} weitere)`;
  }

  return baseLabel;
};

const getVacationDetails = (conflicts?: VacationConflict[]) => {
  if (!conflicts || conflicts.length === 0) {
    return VACATION_PLACEHOLDER;
  }

  return conflicts
    .map((conflict) => {
      const range = formatVacationRange(conflict);
      const statusLabel = getVacationStatusLabel(conflict.status);
      const baseLabel = `${range}${statusLabel ? ` (${statusLabel})` : ''}`.trim();
      return conflict.reason ? `${baseLabel} – ${conflict.reason}` : baseLabel;
    })
    .join('\n');
};

const getPdfVacationDetails = (sample: SampleForReport) => {
  const monthlyVacations = sample.vacations_in_month ?? [];

  if (monthlyVacations.length === 0) {
    return VACATION_PLACEHOLDER;
  }

  const conflictIds = new Set((sample.vacation_conflicts ?? []).map((vacation) => vacation.id));

  return monthlyVacations
    .map((vacation) => {
      const range = formatVacationRange(vacation);
      const conflictLabel = conflictIds.has(vacation.id) ? 'Überschneidet Probe' : 'Kein Konflikt';
      const infoParts = [conflictLabel].filter(Boolean);
      const info = infoParts.length > 0 ? ` (${infoParts.join(', ')})` : '';
      const reason = vacation.reason ? ` – ${vacation.reason}` : '';
      return `${range}${info}${reason}`;
    })
    .join('\n');
};

const groupVacationsByCustomer = (vacations: VacationConflict[]) => {
  return vacations.reduce<Record<string, VacationConflict[]>>((acc, vacation) => {
    const customerId = vacation.customer_id;
    if (!customerId) {
      return acc;
    }

    if (!acc[customerId]) {
      acc[customerId] = [];
    }

    acc[customerId].push(vacation);
    return acc;
  }, {});
};

const formatCustomerName = (sample: SampleForReport) => {
  const parts = [
    sample.customer_last_name?.trim(),
    sample.customer_first_name?.trim(),
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : VACATION_PLACEHOLDER;
};

const generatePDF = (samples: SampleForReport[], monthName: string, organizationName: string) => {
  const doc = new jsPDF({ orientation: 'landscape' });

  doc.setFontSize(16);
  doc.text(organizationName || 'Organisation', 14, 20);
  doc.setFontSize(12);
  doc.text(`Probenliste: ${monthName}`, 14, 30);

  autoTable(doc, {
    startY: 40,
    head: [['Nr.', 'Kunde', 'Probenart', 'Geplant', 'Einbestellt', 'Status', 'Urlaub']],
    body: samples.map((sample, index) => [
      String(index + 1),
      formatCustomerName(sample),
      sample.sample_type_name ?? VACATION_PLACEHOLDER,
      formatPdfDateLabel(sample.scheduled_date),
      formatDateLabel(sample.appointment_date),
      getStatusLabel(sample.status),
      getPdfVacationDetails(sample),
    ]),
    styles: {
      cellWidth: 'wrap',
      overflow: 'linebreak',
    },
    columnStyles: {
      0: { cellWidth: 12 },
      6: { cellWidth: 120 },
    },
    didParseCell: (data) => {
      if (data.section !== 'body') return;

      const sample = samples[data.row.index];
      const fillColor = getPdfStatusRowFill(sample?.status);

      if (fillColor) {
        data.cell.styles.fillColor = false;
      }
    },
    willDrawCell: (data) => {
      if (data.section !== 'body') return;

      const sample = samples[data.row.index];
      const fillColor = getPdfStatusRowFill(sample?.status);
      if (!fillColor) return;

      const [c, m, y, k] = fillColor;
      data.doc.setFillColor(c, m, y, k);
      data.doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
    },
  });

  doc.save(`Probenliste_${monthName.replace(/\s+/g, '_')}.pdf`);
};

const MonthlyListModal: React.FC<MonthlyListModalProps> = ({ show, onClose }) => {
  const supabase = useSupabase();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState<string>(new Date().toISOString().split('T')[0].substring(0, 7));
  const [samples, setSamples] = useState<SampleForReport[]>([]);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);
  // Aktiviert den Standortfilter nur bei gebuchtem `feature_locations`.
  const [hasLocationFeature, setHasLocationFeature] = useState(false);
  // Enthält alle auswählbaren Standorte der Organisation.
  const [organizationLocations, setOrganizationLocations] = useState<OrganizationLocationOption[]>([]);
  // Speichert den aktuell ausgewählten Standortfilter (leer = alle, MAIN_LOCATION_FILTER = Hauptstandort).
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('');

  useEffect(() => {
    if (show) {
      loadOrganizationInfo();
    }
  }, [show]);

  useEffect(() => {
    if (show && month) {
      loadSamples();
    }
  }, [show, month, selectedLocationFilter]);

  useEffect(() => {
    if (show) {
      loadLocationConfiguration();
    }
  }, [show]);

  async function loadOrganizationInfo() {
    try {
      const { data: orgUser, error: orgUserError } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user?.id)
        .single();

      if (orgUserError) throw orgUserError;

      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('name, logo_url, logo_scale_width, logo_scale_height')
        .eq('id', orgUser.organization_id)
        .single();

      if (orgError) throw orgError;

      setOrganizationName(org.name);
      setOrganizationLogo(org.logo_url);
    } catch (err) {
      console.error('Error loading organization info:', err);
    }
  }

  async function loadLocationConfiguration() {
    try {
      const { data: orgUser, error: orgUserError } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user?.id)
        .single();

      if (orgUserError) throw orgUserError;

      const currentOrganizationId = orgUser.organization_id;

      // Prüft, ob der Standortfilter für diese Organisation freigeschaltet ist.
      const { data: featureData, error: featureError } = await supabase
        .from('organization_features')
        .select('feature_locations')
        .eq('organization_id', currentOrganizationId)
        .maybeSingle();

      if (featureError) throw featureError;

      const locationFeatureEnabled = Boolean(featureData?.feature_locations);
      setHasLocationFeature(locationFeatureEnabled);

      if (!locationFeatureEnabled) {
        setOrganizationLocations([]);
        setSelectedLocationFilter('');
        return;
      }

      // Lädt die Standortoptionen für den zusätzlichen Monatslisten-Filter.
      const { data: locationsData, error: locationsError } = await supabase
        .from('organization_locations')
        .select('id, name')
        .eq('organization_id', currentOrganizationId)
        .order('name', { ascending: true });

      if (locationsError) throw locationsError;

      setOrganizationLocations((locationsData || []) as OrganizationLocationOption[]);
    } catch (locationConfigError) {
      console.error('Error loading location configuration:', locationConfigError);
      setHasLocationFeature(false);
      setOrganizationLocations([]);
      setSelectedLocationFilter('');
    }
  }

  async function loadSamples() {
    setLoading(true);
    setError(null);

    try {
      const [year, monthIndex] = month.split('-').map(Number);
      const startDate = format(new Date(year, monthIndex - 1, 1), 'yyyy-MM-dd');
      const endDate = format(new Date(year, monthIndex, 0), 'yyyy-MM-dd');

      const { data: orgUser, error: orgUserError } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user?.id)
        .single();

      if (orgUserError) throw orgUserError;

      let customerIdFilter: string[] | null = null;

      // Wendet optional den Standortfilter auf die zugrundeliegenden Kunden an.
      if (hasLocationFeature && selectedLocationFilter) {
        let customerLocationQuery = supabase
          .from('customers')
          .select('id')
          .eq('organization_id', orgUser.organization_id);

        // Hauptstandort bedeutet: Kunden ohne Standort-Zuordnung.
        if (selectedLocationFilter === MAIN_LOCATION_FILTER) {
          customerLocationQuery = customerLocationQuery.is('location_id', null);
        } else {
          customerLocationQuery = customerLocationQuery.eq('location_id', selectedLocationFilter);
        }

        const { data: filteredCustomers, error: filteredCustomersError } = await customerLocationQuery;

        if (filteredCustomersError) throw filteredCustomersError;

        customerIdFilter = (filteredCustomers || []).map((customer) => customer.id);

        if (customerIdFilter.length === 0) {
          setSamples([]);
          return;
        }
      }

      let sampleQuery = supabase
        .from('sample_details')
        .select('*')
        .eq('organization_id', orgUser.organization_id)
        .gte('scheduled_date', startDate)
        .lte('scheduled_date', endDate)
        .order('scheduled_date', { ascending: true });

      if (customerIdFilter) {
        sampleQuery = sampleQuery.in('customer_id', customerIdFilter);
      }

      const { data: samplesData, error: samplesError } = await sampleQuery;

      if (samplesError) throw samplesError;

      const safeData = (Array.isArray(samplesData) ? samplesData : []) as SampleForReport[];

      let vacations: VacationConflict[] = [];
      try {
        const chunkSize = 100;
        const customerIds = Array.from(
          new Set(safeData.map((sample) => sample.customer_id).filter(Boolean))
        ) as string[];
        for (let i = 0; i < customerIds.length; i += chunkSize) {
          const chunk = customerIds.slice(i, i + chunkSize);
          const { data: vacationData, error: vacationError } = await supabase
            .from('vacation_requests')
            .select('id, customer_id, contract_id, start_date, end_date, status, reason')
            .eq('organization_id', orgUser.organization_id)
            .in('status', ['approved', 'pending', 'planned', 'submitted'])
            .in('customer_id', chunk)
            .lte('start_date', endDate)
            .gte('end_date', startDate);

          if (vacationError) throw vacationError;

          if (vacationData && vacationData.length > 0) {
            vacations = vacations.concat(vacationData as VacationConflict[]);
          }
        }
      } catch (vacationError) {
        console.error('Error loading vacation conflicts:', vacationError);
      }

      const annotatedSamples = annotateVacationConflicts<SampleForReport>(safeData, vacations);

      const vacationsByCustomer = groupVacationsByCustomer(vacations);
      Object.values(vacationsByCustomer).forEach((customerVacations) => {
        customerVacations.sort(
          (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
        );
      });

      const samplesWithMonthlyVacations = annotatedSamples.map((sample) => {
        const customerId = sample.customer_id ?? undefined;
        const monthlyVacations = customerId ? vacationsByCustomer[customerId] ?? [] : [];

        return {
          ...sample,
          vacations_in_month: monthlyVacations,
        };
      });

      setSamples(samplesWithMonthlyVacations);
    } catch (err) {
      console.error('Error loading samples:', err);
      setError('Fehler beim Laden der Proben');
    } finally {
      setLoading(false);
    }
  }

  const handleDownloadPdf = async () => {
    const element = document.getElementById('pdf-export');
    if (!element) return;

    const canvas = await html2canvas(element as HTMLElement);
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4',
    });

    const width = pdf.internal.pageSize.getWidth();
    const height = (canvas.height * width) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 20, width, height);
    pdf.save(`Probenliste_${month}.pdf`);
    await logDocumentExportForCurrentUser(supabase, {
      userId: user?.id,
      caseId: month,
      documentType: 'monthly_sample_list_pdf',
      documentId: month,
      actionType: 'export',
      channel: 'browser_download',
      technicalSource: 'frontend:monthly-list-modal',
      context: {
        export_mode: 'canvas',
      },
    });
  };

  const handleGeneratePdf = async () => {
    generatePDF(samples, monthName, organizationName);
    await logDocumentExportForCurrentUser(supabase, {
      userId: user?.id,
      caseId: month,
      documentType: 'monthly_sample_list_pdf',
      documentId: month,
      actionType: 'export',
      channel: 'browser_download',
      technicalSource: 'frontend:monthly-list-modal',
      context: {
        export_mode: 'table',
      },
    });
  };

  if (!show) return null;

  const monthName = new Date(`${month}-01`).toLocaleDateString('de-DE', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <FileText className="h-5 w-5 mr-2 text-primary-600" />
            Monatliche Probenliste: {monthName}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-grow">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Monat auswählen
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="pl-10 w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              />
            </div>
          </div>
          {hasLocationFeature && (
            <div className="flex-grow">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Standort filtern
              </label>
              <select
                value={selectedLocationFilter}
                onChange={(e) => setSelectedLocationFilter(e.target.value)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              >
                <option value="">Alle Standorte</option>
                <option value={MAIN_LOCATION_FILTER}>Hauptstandort</option>
                {organizationLocations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex gap-2 mt-4 sm:mt-0">
            <button
              onClick={() => void handleGeneratePdf()}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              PDF generieren
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : samples.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              Keine Proben für {monthName} gefunden
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div id="pdf-export" className="mb-4" style={{ display: 'block' }}>
              <div className="flex items-center justify-between">
                {organizationLogo && (
                  <img src={organizationLogo} alt="Logo" className="h-16 object-contain" />
                )}
                <div className="text-right">
                  <h2 className="text-xl font-bold">{organizationName}</h2>
                  <h3 className="text-lg">Probenliste {monthName}</h3>
                </div>
              </div>
            </div>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kunde
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Probenart
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Geplant
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Einbestellt
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Urlaub
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                {samples.map((sample) => {
                  const hasVacationConflict = sample.vacation_conflicts && sample.vacation_conflicts.length > 0;
                  return (
                    <tr key={sample.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {formatCustomerName(sample)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {sample.sample_type_name ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {sample.scheduled_date ? new Date(sample.scheduled_date).toLocaleDateString('de-DE') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {sample.appointment_date ? new Date(sample.appointment_date).toLocaleDateString('de-DE') : '-'}
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm"
                        title={hasVacationConflict ? getVacationDetails(sample.vacation_conflicts) : undefined}
                      >
                        {hasVacationConflict ? (
                          <div className="flex items-center text-yellow-700 dark:text-yellow-300">
                            <Umbrella className="h-4 w-4 mr-1" />
                            <span>{getVacationSummary(sample.vacation_conflicts)}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-600">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(sample.status)}`}>
                          {getStatusLabel(sample.status)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyListModal;
